import { ArrowDropDownRounded, ArrowDropUpRounded, SubdirectoryArrowRight } from '@mui/icons-material';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { FC, ForwardedRef, MutableRefObject, RefObject, useEffect, useState } from 'react';

import { scrollOffset } from '_core/hooks/use-scroll-with-refs';
import { LegalSection } from '_features/legal/legal.types';

import * as S from './styles';

export type SectionRef = {
  ref: RefObject<HTMLDivElement>;
  subRefs: RefObject<HTMLDivElement>[];
};

type AsideBlockProps = {
  pageContent: LegalSection[];
  sectionRefs: MutableRefObject<SectionRef[]>;
  scrollableCtn?: ForwardedRef<HTMLDivElement>;
};

type positionRef = {
  position: number;
  subPositions: number[];
};

export const AsideBlock: FC<AsideBlockProps> = (props) => {
  const { pageContent, scrollableCtn, sectionRefs } = props;
  const { t } = useTranslation();
  const [positions, setPositions] = useState<positionRef[]>();
  const [subsectionOpen, setSubsectionOpen] = useState<LegalSection['id'] | null>(null);

  const openSubsection = (id: LegalSection['id']) => {
    if (id === subsectionOpen) {
      setSubsectionOpen(null);
    } else setSubsectionOpen(id);
  };

  const scrollYTo = (position: number, id?: LegalSection['id']) => {
    (scrollableCtn as MutableRefObject<HTMLDivElement>).current?.scrollTo({ top: position, behavior: 'smooth' });
    if (id) {
      openSubsection(id);
    }
  };

  const handlePositions = () => {
    setPositions(
      pageContent.map((section: LegalSection, index: number) => {
        const sectionPosition = sectionRefs?.current[index]?.ref?.current?.getBoundingClientRect()?.top || 0;
        return {
          position:
            sectionPosition + (scrollableCtn as MutableRefObject<HTMLDivElement>).current.scrollTop - scrollOffset,
          subPositions: sectionRefs?.current[index]?.subRefs?.map((subRef: RefObject<HTMLDivElement>) => {
            const subsectionPosition = subRef?.current?.getBoundingClientRect()?.top || 0;
            return (
              subsectionPosition + (scrollableCtn as MutableRefObject<HTMLDivElement>).current.scrollTop - scrollOffset
            );
          }),
        };
      }),
    );
  };

  useEffect(() => {
    handlePositions();
    (scrollableCtn as MutableRefObject<HTMLDivElement>).current?.addEventListener('scroll', handlePositions, {
      passive: true,
    });
    return () => {
      (scrollableCtn as MutableRefObject<HTMLDivElement>).current?.removeEventListener('scroll', handlePositions);
    };
  }, []);

  return (
    <S.StickyBlock className="overflow-auto shadow-2 rounded-lg px-2 py-4 sticky border border-secondary-300">
      <header className="pt-4 pb-2 px-2">
        <p className="bold h4">{t('legal:index')}</p>
      </header>
      {pageContent.map((section: LegalSection, index: number) => {
        const sectionIndex = index;
        if (!section.title) return null;
        else {
          return (
            <div className="my-1" key={`nav-block-item-${section.id}`}>
              <S.IndexItem
                className="cursor-pointer py-2 px-3 rounded-md layout-row layout-align-start-center"
                selected={subsectionOpen === section.id}>
                <div
                  className="flex-1 small lh-3"
                  onClick={() => scrollYTo(positions ? positions[sectionIndex].position : 0, section.id)}
                  onKeyDown={() => scrollYTo(positions ? positions[sectionIndex].position : 0, section.id)}
                  role="button"
                  tabIndex={0}
                  dangerouslySetInnerHTML={{ __html: section.title as string }}
                />
                {section.subsections && (
                  <div
                    onClick={() => openSubsection(section.id)}
                    onKeyDown={() => openSubsection(section.id)}
                    role="button"
                    tabIndex={0}
                    className="layout-column layout-align-center-center">
                    {section.id === subsectionOpen ? (
                      <ArrowDropUpRounded style={{ fontSize: 24 }} className="text-primary-500" />
                    ) : (
                      <ArrowDropDownRounded style={{ fontSize: 24 }} className="text-secondary-900" />
                    )}
                  </div>
                )}
              </S.IndexItem>
              {section.subsections && subsectionOpen === section.id && (
                <S.Gradient className="relative">
                  <S.ScrollingArea className="overflow-y-auto my-2 p-2 rounded-md bg-secondary-50 pb-12">
                    {section.subsections?.map((subsection: LegalSection, index: number) => {
                      const subsectionIndex = index;
                      return (
                        <div className="my-1" key={`nav-block-subitem-${subsection.id}`}>
                          <S.IndexItem
                            onClick={() =>
                              scrollYTo(positions ? positions[sectionIndex].subPositions[subsectionIndex] : 0)
                            }
                            onKeyDown={() =>
                              scrollYTo(positions ? positions[sectionIndex].subPositions[subsectionIndex] : 0)
                            }
                            role="button"
                            tabIndex={0}
                            className="cursor-pointer py-2 px-3 rounded-md layout-row layout-align-start-center">
                            <SubdirectoryArrowRight style={{ fontSize: 14 }} className="mr-2 text-secondary-500" />
                            <div
                              className="small text-truncate"
                              dangerouslySetInnerHTML={{ __html: subsection.title as string }}
                            />
                          </S.IndexItem>
                        </div>
                      );
                    })}
                  </S.ScrollingArea>
                </S.Gradient>
              )}
            </div>
          );
        }
      })}
    </S.StickyBlock>
  );
};
